import React, { useState } from 'react';
import { ICON } from '../icons/iconIds';
import IconText from './icons/IconText';
import IconButton from './icons/IconButton';
import { useTheme } from './ThemeProvider';
import Button from './ui/Button';
import Card from './ui/Card';
import './ThemeSettings.css';

const ThemeSettings = ({ onClose }) => {
  const { 
    theme, 
    isDark, 
    toggleTheme, 
    colors, 
    updateCustomColor, 
    resetTheme, 
    applyPreset 
  } = useTheme();

  const [activeTab, setActiveTab] = useState('presets');

  const colorPresets = [
    { 
      name: 'Default Gold', 
      id: 'default', 
      primary: '#f59e0b',
      description: 'Original Selkirk Copper gold'
    },
    { 
      name: 'Copper', 
      id: 'copper', 
      primary: '#b45309',
      description: 'Darker copper tone'
    },
    { 
      name: 'Bright Gold', 
      id: 'gold', 
      primary: '#fbbf24',
      description: 'Lighter, brighter gold'
    },
    { 
      name: 'Professional Blue', 
      id: 'blue', 
      primary: '#3b82f6',
      description: 'Corporate blue theme'
    }
  ];

  const customizableColors = [
    { key: '500', label: 'Primary Color', description: 'Main brand color' },
    { key: '400', label: 'Light Accent', description: 'Hover states' },
    { key: '600', label: 'Dark Accent', description: 'Active states' },
  ];

  return (
    <div className="theme-settings-overlay">
      <div className="theme-settings-modal">
        <Card variant="elevated" className="theme-settings-card">
          {/* Header */}
          <div className="settings-header">
            <IconText icon={ICON.uiThemeLight} as="h2">Theme Settings</IconText>
            <IconButton icon={ICON.uiClose} className="close-button" onClick={onClose} ariaLabel="Close" />
          </div>

          {/* Tabs */}
          <div className="settings-tabs">
            <button 
              className={`tab-button ${activeTab === 'presets' ? 'active' : ''}`}
              onClick={() => setActiveTab('presets')}
            >
              Color Presets
            </button>
            <button 
              className={`tab-button ${activeTab === 'custom' ? 'active' : ''}`}
              onClick={() => setActiveTab('custom')}
            >
              Custom Colors
            </button>
            <button 
              className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
              onClick={() => setActiveTab('general')}
            >
              General
            </button>
          </div>

          {/* Tab Content */}
          <div className="settings-content">
            {activeTab === 'presets' && (
              <div className="presets-section">
                <h3>Choose a Color Preset</h3>
                <p>Select a predefined color scheme for your platform</p>
                
                <div className="presets-grid">
                  {colorPresets.map((preset) => (
                    <div key={preset.id} className="preset-card">
                      <div className="preset-preview">
                        <div 
                          className="preset-color-circle"
                          style={{ backgroundColor: preset.primary }}
                        ></div>
                        <div className="preset-info">
                          <h4>{preset.name}</h4>
                          <p>{preset.description}</p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => applyPreset(preset.id)}
                        className="apply-preset-btn"
                      >
                        Apply
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'custom' && (
              <div className="custom-section">
                <h3>Custom Color Settings</h3>
                <p>Fine-tune individual colors to match your preferences</p>
                
                <div className="color-controls">
                  {customizableColors.map((colorDef) => (
                    <div key={colorDef.key} className="color-control">
                      <div className="color-info">
                        <label className="color-label">{colorDef.label}</label>
                        <span className="color-description">{colorDef.description}</span>
                      </div>
                      <div className="color-input-group">
                        <input
                          type="color"
                          value={colors.primary[colorDef.key] || '#f59e0b'}
                          onChange={(e) => updateCustomColor(colorDef.key, e.target.value)}
                          className="color-picker"
                        />
                        <input
                          type="text"
                          value={colors.primary[colorDef.key] || '#f59e0b'}
                          onChange={(e) => updateCustomColor(colorDef.key, e.target.value)}
                          className="color-text-input"
                          placeholder="#f59e0b"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="custom-actions">
                  <Button variant="danger" onClick={resetTheme}>
                    Reset to Default
                  </Button>
                </div>
              </div>
            )}

            {activeTab === 'general' && (
              <div className="general-section">
                <h3>General Settings</h3>
                <p>Configure overall theme preferences</p>
                
                <div className="setting-group">
                  <div className="setting-item">
                    <div className="setting-info">
                      <label className="setting-label">Theme Mode</label>
                      <span className="setting-description">
                        Switch between light and dark themes
                      </span>
                    </div>
                    <div className="theme-toggle">
                      <button 
                        className={`theme-option ${isDark ? 'active' : ''}`}
                        onClick={() => !isDark && toggleTheme()}
                      >
                        <IconText icon={ICON.uiThemeDark} iconSize={16}>Dark</IconText>
                      </button>
                      <button 
                        className={`theme-option ${!isDark ? 'active' : ''}`}
                        onClick={() => isDark && toggleTheme()}
                      >
                        <IconText icon={ICON.uiThemeLight} iconSize={16}>Light</IconText>
                      </button>
                    </div>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <label className="setting-label">Animations</label>
                      <span className="setting-description">
                        Enable or disable interface animations
                      </span>
                    </div>
                    <Button variant="ghost" size="sm">
                      Configure
                    </Button>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <label className="setting-label">Accessibility</label>
                      <span className="setting-description">
                        High contrast and accessibility options
                      </span>
                    </div>
                    <Button variant="ghost" size="sm">
                      Configure
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="settings-footer">
            <div className="footer-info">
              <span>Theme changes are saved automatically</span>
            </div>
            <div className="footer-actions">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ThemeSettings;
